import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTheme, mergeStyleSets } from '@uifabric/styling';
import { Checkbox, FocusZoneDirection, FocusZoneTabbableElements, Layer, MessageBarType } from 'office-ui-fabric-react';
import OutsideClickHandler from 'react-outside-click-handler';
import useWindowSize from '../../../hooks/useWindowSize';
import { ActionButton } from '../../Button/Action';
import { clearIconButtonStyles } from '../../ClearIconButtonStyles';
import { FocusZone } from '../../FocusZone';
import { Label } from '../../Label';
import { List } from '../../List';
import { MessageBar } from '../../MessageBar';
import { Stack } from '../../Stack';
import { ElevatedStack } from '../../Stack/ElevatedStack';
import { TextField } from '../../TextField';
import { SearchBox } from '../SearchBox';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const SearchMultiDropdown = props => {
  var _componentRef$current2, _componentRef$current3, _componentRef$current4, _componentRef$current5, _componentRef$current6, _componentRef$current7;
  const [isListOpen, setIsListOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOptionsString, setSelectedOptionsString] = useState('');
  const textFieldRef = useRef(null);
  const componentRef = useRef(null);
  const searchBoxRef = useRef(null);
  const windowSize = useWindowSize();
  const optionList = props.optionList.map(item => item);
  const listOverRef = useRef(null);
  const listUnderRef = useRef(null);
  useEffect(() => {
    if (isListOpen) {
      var _searchBoxRef$current;
      (_searchBoxRef$current = searchBoxRef.current) === null || _searchBoxRef$current === void 0 || _searchBoxRef$current.focus();
    }
  }, [isListOpen]);
  const formatValueUsingRenderMethods = candidate => {
    return props.renderInput ? props.renderInput(candidate) : props.renderData(candidate);
  };
  useEffect(() => {
    const userInputValue = props.value.map(value => formatValueUsingRenderMethods(value)).join(', ');
    setSelectedOptionsString(userInputValue);
  }, [props.value]);
  const filterList = query => {
    return props.customSearch ? props.customSearch(query) : optionList.filter(s => props.renderData(s).toLowerCase().startsWith(query.toLowerCase()));
  };
  const [filteredItems, setFilteredItems] = useState(filterList(searchQuery));
  useEffect(() => {
    const filteredList = filterList(searchQuery);
    setFilteredItems(filteredList);
  }, [searchQuery, JSON.stringify(props.value), JSON.stringify(optionList)]);
  const rowHeight = 41;
  const listHeight = props.maxItemsOverlay && filteredItems.length > props.maxItemsOverlay ? props.maxItemsOverlay * rowHeight : !filteredItems.length ? 32 : filteredItems.length * rowHeight;
  const shouldListBePositionedOnTop = useMemo(() => {
    if (props.optionListPosition === 'auto') {
      var _componentRef$current;
      const domRect = (_componentRef$current = componentRef.current) === null || _componentRef$current === void 0 ? void 0 : _componentRef$current.getBoundingClientRect();
      return domRect && windowSize.height < domRect.bottom + listHeight + 48;
    }
    return props.optionListPosition === 'top' ? true : false;
  }, [isListOpen, componentRef, windowSize, listHeight]);
  const listWrapperStyles = {
    root: {
      fontFamily: getTheme().fonts.medium.fontFamily,
      fontSize: 14,
      boxShadow: 'none',
      width: 'calc(100% - 2px)',
      marginLeft: 1,
      zIndex: 2,
      overflowY: 'auto',
      height: listHeight
    }
  };
  const handleInputChange = (_event, value) => {
    setSearchQuery(value !== null && value !== void 0 ? value : '');
    setFilteredItems(filterList(searchQuery || ''));
  };
  const handlePicked = useCallback((value, currentValues) => {
    var _props$optionIdentity;
    const optionIdentityPredicate = (_props$optionIdentity = props.optionIdentityPredicate) !== null && _props$optionIdentity !== void 0 ? _props$optionIdentity : props.renderData;
    const isAlreadySelected = props.value.some(v => optionIdentityPredicate(v) === optionIdentityPredicate(value)) || currentValues.find(item => optionIdentityPredicate(item) === optionIdentityPredicate(value));
    if (isAlreadySelected) {
      props.onChange(currentValues.filter(item => optionIdentityPredicate(item) !== optionIdentityPredicate(value)));
    } else {
      props.onChange([...currentValues, value]);
    }
  }, [JSON.stringify(props.value), props.onChange, props.renderData]);
  const handleInputMouseUp = () => {
    openListAndClearSearchQuery();
  };
  const openListAndClearSearchQuery = () => {
    setSearchQuery('');
    setIsListOpen(true);
  };
  const actionButtonStyles = {
    root: {
      height: 30,
      maxHeight: 30
    },
    icon: {
      fontSize: 12
    }
  };
  const DropButton = /*#__PURE__*/_jsx(ActionButton, {
    disabled: props.disabled,
    iconProps: {
      iconName: 'ChevronDown'
    },
    styles: actionButtonStyles,
    onClick: () => {
      openListAndClearSearchQuery();
    },
    "data-is-focusable": false,
    tabIndex: -1
  });
  const PickButton = /*#__PURE__*/_jsx(ActionButton, {
    disabled: props.disabled,
    iconProps: {
      iconName: 'ChevronUp'
    },
    styles: actionButtonStyles,
    "data-is-focusable": false,
    tabIndex: -1,
    onClick: () => {
      setIsListOpen(false);
    }
  });
  const ClearButton = /*#__PURE__*/_jsx(ActionButton, {
    disabled: props.disabled,
    iconProps: {
      iconName: 'Cancel'
    },
    styles: clearIconButtonStyles,
    "data-is-focusable": false,
    tabIndex: -1,
    onClick: event => {
      var _props$onClear;
      event.stopPropagation();
      (_props$onClear = props.onClear) === null || _props$onClear === void 0 || _props$onClear.call(props);
    }
  });
  const renderListItem = useCallback((item, index, isSelected) => {
    const itemStyles = {
      root: {
        cursor: 'pointer',
        flexDirection: 'row',
        padding: 7,
        height: 'fit-content',
        wordBreak: 'break-word',
        borderBottom: props.firstItemDivider && index === 0 ? '1px solid #e0e0e0' : undefined,
        selectors: {
          ':hover': {
            backgroundColor: getTheme().palette.neutralLighter
          },
          ':focus': {
            backgroundColor: getTheme().palette.neutralLighter,
            outline: 'none'
          }
        }
      }
    };
    return /*#__PURE__*/_jsx(Stack, {
      styles: itemStyles,
      "data-title": props.renderData(item),
      "data-is-focusable": true,
      tabIndex: 1,
      onKeyDown: event => handleListItemSpecialKeysPress(event, item, props.value),
      onClick: () => {
        handlePicked(item, props.value);
      },
      children: /*#__PURE__*/_jsx(FocusZone, {
        disabled: true,
        children: /*#__PURE__*/_jsxs(Label, {
          styles: {
            root: {
              width: '100%',
              height: 'fit-content',
              wordBreak: 'break-word',
              flexDirection: 'row',
              display: 'flex',
              fontWeight: 'normal',
              cursor: 'pointer'
            }
          },
          onClick: e => {
            e === null || e === void 0 || e.preventDefault();
            e === null || e === void 0 || e.stopPropagation();
            handlePicked(item, props.value);
          },
          children: [/*#__PURE__*/_jsx(Checkbox, {
            checked: isSelected,
            styles: {
              root: {
                width: 21,
                height: 21,
                marginRight: 5
              }
            }
          }), props.renderData(item)]
        })
      })
    }, index);
  }, [JSON.stringify(optionList), JSON.stringify(props.value), handlePicked]);
  const handleInputSpecialKeysPress = event => {
    switch (event.key) {
      case 'Escape':
        onEscapeClick();
        event.stopPropagation();
        return event;
      case 'Enter':
        openListAndClearSearchQuery();
        event.stopPropagation();
        return event;
      default:
        return event;
    }
  };
  const onEscapeClick = () => {
    var _textFieldRef$current;
    setIsListOpen(false);
    (_textFieldRef$current = textFieldRef.current) === null || _textFieldRef$current === void 0 || _textFieldRef$current.focus();
  };
  const handleListItemSpecialKeysPress = (event, value, currentValues) => {
    switch (event.key) {
      case 'Enter':
        handlePicked(value, currentValues);
        event.stopPropagation();
        return event;
      case ' ':
        handlePicked(value, currentValues);
        event.stopPropagation();
        return event;
      case 'Escape':
        onEscapeClick();
        event.stopPropagation();
        return event;
      default:
        return event;
    }
  };
  const getTextFieldButton = () => isListOpen ? PickButton : props.onClear && props.value.length ? ClearButton : DropButton;
  const searchBoxStyles = {
    root: {
      width: "100%",
      display: !isListOpen ? 'none' : 'flex',
      fontFamily: getTheme().fonts.medium.fontFamily,
      fontSize: 14,
      zIndex: 2,
      marginBottom: 2
    }
  };
  const getSelectedSortedToTop = () => {
    var _props$optionIdentity2;
    const newOptions = [];
    const identityPredicate = (_props$optionIdentity2 = props.optionIdentityPredicate) !== null && _props$optionIdentity2 !== void 0 ? _props$optionIdentity2 : props.renderData;
    props.value.forEach(item => {
      const selectedItem = filteredItems.find(option => identityPredicate(option) === identityPredicate(item));
      if (selectedItem) {
        newOptions.push(selectedItem);
      }
    });
    const otherOptions = filteredItems.filter(item => newOptions.indexOf(item) === -1);
    newOptions.push(...otherOptions);
    return newOptions;
  };
  const checkIsOptionSelected = item => {
    var _props$optionIdentity3;
    const optionIdentityPredicate = (_props$optionIdentity3 = props.optionIdentityPredicate) !== null && _props$optionIdentity3 !== void 0 ? _props$optionIdentity3 : props.renderData;
    return !!props.value.find(elem => optionIdentityPredicate(elem) === optionIdentityPredicate(item));
  };
  const SearchField = /*#__PURE__*/_jsx(SearchBox, {
    componentRef: searchBoxRef,
    value: searchQuery,
    onChange: (_e, v) => {
      setSearchQuery(v !== null && v !== void 0 ? v : '');
    },
    styles: searchBoxStyles,
    placeholder: props.searchPlaceholder,
    "data-id": props.dataId ? props.dataId + '-search' : undefined,
    onEscape: onEscapeClick
  });
  const SelectedOptionsTextField = /*#__PURE__*/_jsx(TextField, {
    label: props.label,
    disabled: props.disabled,
    styles: mergeStyleSets({
      suffix: {
        backgroundColor: 'transparent',
        padding: 0
      },
      root: {
        width: '100%'
      }
    }, props.styles),
    onRenderSuffix: getTextFieldButton,
    onKeyDown: event => {
      if (props.onKeyDown) props.onKeyDown(event);
      handleInputSpecialKeysPress(event);
    },
    onMouseUp: handleInputMouseUp,
    placeholder: props.placeholder,
    onChange: handleInputChange,
    autoComplete: 'off',
    value: selectedOptionsString,
    id: props.id,
    componentRef: textFieldRef,
    readOnly: true,
    "data-id": props.dataId,
    "data-is-focusable": true
  });
  const ListUnderTextField = /*#__PURE__*/_jsx(Layer, {
    children: /*#__PURE__*/_jsx("div", {
      ref: listUnderRef,
      children: /*#__PURE__*/_jsx(ElevatedStack, {
        "data-id": "".concat(props.dataId, "-dropdown-list"),
        styles: {
          root: !isListOpen ? {
            display: 'none'
          } : {
            position: 'absolute',
            width: "calc(".concat((_componentRef$current2 = componentRef.current) === null || _componentRef$current2 === void 0 ? void 0 : _componentRef$current2.clientWidth, "px - 2px)"),
            zIndex: 2,
            height: "auto",
            padding: 5,
            top: (_componentRef$current3 = componentRef.current) === null || _componentRef$current3 === void 0 ? void 0 : _componentRef$current3.getBoundingClientRect().bottom,
            left: (_componentRef$current4 = componentRef.current) === null || _componentRef$current4 === void 0 ? void 0 : _componentRef$current4.getBoundingClientRect().left
          }
        },
        children: /*#__PURE__*/_jsxs(FocusZone, {
          direction: FocusZoneDirection.vertical,
          handleTabKey: FocusZoneTabbableElements.all,
          isCircularNavigation: true,
          children: [SearchField, isListOpen && /*#__PURE__*/_jsx(ElevatedStack, {
            styles: listWrapperStyles,
            "data-is-scrollable": true,
            children: !filteredItems.length ? /*#__PURE__*/_jsx(MessageBar, {
              messageBarType: MessageBarType.info,
              "data-id": "field-error",
              children: "No results found"
            }) : /*#__PURE__*/_jsx(List, {
              items: props.selectedItemsAlwaysOnTop ? getSelectedSortedToTop() : filteredItems,
              ignoreScrollingState: true,
              onRenderCell: (item, index) => renderListItem(item, index, checkIsOptionSelected(item)),
              version: optionList
            })
          })]
        })
      })
    })
  });
  const ListOverTextField = /*#__PURE__*/_jsx(Layer, {
    children: /*#__PURE__*/_jsx("div", {
      ref: listOverRef,
      children: /*#__PURE__*/_jsx(ElevatedStack, {
        styles: {
          root: !isListOpen ? {
            display: 'none'
          } : {
            position: 'absolute',
            width: "calc(".concat((_componentRef$current5 = componentRef.current) === null || _componentRef$current5 === void 0 ? void 0 : _componentRef$current5.clientWidth, "px - 2px)"),
            zIndex: 2,
            height: "auto",
            padding: 5,
            bottom: "calc(100vh - ".concat((_componentRef$current6 = componentRef.current) === null || _componentRef$current6 === void 0 ? void 0 : _componentRef$current6.getBoundingClientRect().top, "px)"),
            left: (_componentRef$current7 = componentRef.current) === null || _componentRef$current7 === void 0 ? void 0 : _componentRef$current7.getBoundingClientRect().left
          }
        },
        children: /*#__PURE__*/_jsxs(FocusZone, {
          handleTabKey: FocusZoneTabbableElements.all,
          direction: FocusZoneDirection.vertical,
          isCircularNavigation: true,
          children: [isListOpen && /*#__PURE__*/_jsx(ElevatedStack, {
            styles: listWrapperStyles,
            "data-is-scrollable": true,
            children: !filteredItems.length ? /*#__PURE__*/_jsx(MessageBar, {
              messageBarType: MessageBarType.info,
              "data-id": "field-error",
              children: "No results found"
            }) : /*#__PURE__*/_jsx(List, {
              items: props.selectedItemsAlwaysOnTop ? getSelectedSortedToTop() : filteredItems,
              ignoreScrollingState: true,
              onRenderCell: (item, index) => renderListItem(item, index, checkIsOptionSelected(item)),
              version: optionList
            })
          }), SearchField]
        })
      })
    })
  });
  return /*#__PURE__*/_jsx(OutsideClickHandler, {
    onOutsideClick: e => {
      const isListOverClicked = !!listOverRef.current && listOverRef.current.contains(e.target);
      const isListUnderClicked = !!listUnderRef.current && listUnderRef.current.contains(e.target);
      if (isListOverClicked || isListUnderClicked) return;
      setIsListOpen(false);
    },
    children: /*#__PURE__*/_jsx("div", {
      ref: componentRef,
      children: /*#__PURE__*/_jsxs(Stack, {
        styles: {
          root: {
            display: 'block',
            position: 'relative'
          }
        },
        children: [shouldListBePositionedOnTop && ListOverTextField, SelectedOptionsTextField, !shouldListBePositionedOnTop && ListUnderTextField]
      })
    })
  });
};
SearchMultiDropdown.displayName = 'SearchDropdown';
SearchMultiDropdown.defaultProps = {
  maxItemsOverlay: 5,
  optionListPosition: 'auto'
};